import { type UseFormReturnType } from '@mantine/form';
import { isApiErrorResult, ToError } from '@scoring/api';
import { useSuccessStore } from '@scoring/state';
import { useState } from 'react';

export type EntityFormSubmitOptions = {
    autoCreate?: boolean;
};

export type FormMode = 'create' | 'update';

export const useEntityFormSubmit = <TFormType extends { id?: string }>({
    form,
    onCreate,
    onUpdate,
    afterSubmit,
    options = { autoCreate: false },
}: {
    form: UseFormReturnType<TFormType>;
    onCreate?: (values: TFormType) => Promise<TFormType>;
    onUpdate?: (values: TFormType) => Promise<void>;
    afterSubmit?: (values: TFormType) => Promise<void>;
    options?: EntityFormSubmitOptions;
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const triggerSuccess = useSuccessStore((state) => state.triggerSuccess);

    const mode: FormMode = !form.getValues().id ? 'create' : 'update';

    const submit = async (manualSubmit: boolean = false): Promise<boolean> => {
        if (loading) return false;

        if (!form.isDirty() && !form.isTouched()) return false;
        const isCreate = !form.getValues().id;

        if (!isCreate || manualSubmit) form.validate();

        if (!form.isValid()) {
            console.log('Form is not valid', form.errors);
            return false;
        }

        const values = form.getValues();

        if (!isCreate && !values.id) return false;

        if (isCreate && !options.autoCreate && !manualSubmit) return false;

        try {
            let didSubmit = false;
            setLoading(true);
            if (isCreate && onCreate) {
                const created = await onCreate(values);
                form.setValues(created);
                didSubmit = true;
            } else {
                if (onUpdate && form.isDirty()) {
                    await onUpdate(values);
                    didSubmit = true;
                }
            }

            if (afterSubmit && didSubmit) {
                await afterSubmit(form.getValues());
            }

            if (didSubmit) {
                form.setInitialValues(form.getValues());
                setError(null);
                triggerSuccess();
                return true;
            }
        } catch (error) {
            if (isApiErrorResult(error)) {
                const err = ToError(error);
                err.validationErrors
                    .filter((ve) => !ve.isGeneral)
                    .forEach((ve) => {
                        const formFieldNormalized = ve.field.replaceAll('[', '.').replaceAll(']', '');
                        form.setFieldError(formFieldNormalized, ve.errors.join(', '));
                    });
                const generalErrors = err.validationErrors
                    .filter((ve) => ve.isGeneral)
                    .map((ve) => ve.errors)
                    .flat()
                    .join(', ');
                setError(generalErrors || err.message || 'An error occurred');
            } else if (typeof error === 'string') {
                setError(error);
            } else if (error instanceof Error) {
                setError(error.message);
            } else {
                setError('Unknown error occurred');
            }
            return false;
        } finally {
            setLoading(false);
            form.resetTouched();
            form.resetDirty();
        }
        return false;
    };

    return {
        loading,
        setLoading,
        submit,
        mode,
        error,
    };
};
